import React, { useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  MapPin,
  Users,
  Building2,
  Phone,
  Mail,
  Globe,
  Edit,
  Trash2,
  Calendar,
  TrendingUp,
  Car,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Filter,
  Download,
  Star,
  Navigation,
  ArrowRight,
  DollarSign
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import usePersistentFilters from '../../hooks/usePersistentFilters';

const RegionalOfficeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const getFormattedDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTodayDateString = () => getFormattedDate(new Date());

  const getPresetDates = (preset) => {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    if (preset === 'all') {
      return {
        startDate: '',
        endDate: ''
      };
    } else if (preset === 'today') {
      start = now;
      end = now;
    } else if (preset === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      start = yesterday;
      end = yesterday;
    } else if (preset === 'thisMonth') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = now;
    }

    return {
      startDate: getFormattedDate(start),
      endDate: getFormattedDate(end)
    };
  };

  const [activePreset, setActivePreset] = useState('today');
  const [dateRange, setDateRange] = useState(() => {
    const todayStr = getTodayDateString();
    return {
      startDate: todayStr,
      endDate: todayStr
    };
  });

  const defaultDriverFilters = {
    search: '',
    status: '',
    isApproved: 'true',
    isBlocked: '',
    page: 1,
    limit: 10
  };

  const defaultTripFilters = {
    search: '',
    status: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 10
  };

  const {
    filters: driverFilters,
    handleFilterChange: handleDriverFilterChange,
    handlePageChange: handleDriverPageChange,
    clearFilters: clearDriverFilters
  } = usePersistentFilters(defaultDriverFilters, `regional-office-${id}-drivers-filters`);

  const {
    filters: tripFilters,
    handleFilterChange: handleTripFilterChange,
    handlePageChange: handleTripPageChange,
    clearFilters: clearTripFilters
  } = usePersistentFilters(defaultTripFilters, `regional-office-${id}-trips-filters`);

  const { data, isLoading, error } = useQuery(
    ['regional-office', id],
    () => api.get(`/regional-offices/${id}`),
    {
      select: (data) => data?.data?.data || data?.data
    }
  );

  // Enhanced drivers query with pagination and filters
  const { data: driversData, isLoading: driversLoading } = useQuery(
    ['regional-office-drivers', id, driverFilters],
    () => api.get(`/regional-offices/${id}/drivers`, { params: driverFilters }),
    {
      select: (data) => data?.data?.data || data?.data,
      enabled: !!id && activeTab === 'drivers'
    }
  );

  // Performance metrics query
  const { data: performanceData, isLoading: performanceLoading } = useQuery(
    ['regional-office-performance', id, dateRange],
    () => api.get(`/regional-offices/${id}/performance`, {
      params: {
        ...(dateRange.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange.endDate ? { endDate: dateRange.endDate } : {})
      }
    }),
    {
      select: (data) => data?.data?.data || data?.data,
      enabled: !!id && activeTab === 'overview'
    }
  );

  // Trips query with pagination and filters
  const { data: tripsData, isLoading: tripsLoading } = useQuery(
    ['regional-office-trips', id, tripFilters],
    () => api.get(`/regional-offices/${id}/trips`, { params: tripFilters }),
    {
      select: (data) => data?.data?.data || data?.data,
      enabled: !!id && activeTab === 'trips'
    }
  );

  const deleteMutation = useMutation(
    (officeId) => api.delete(`/regional-offices/${officeId}`),
    {
      onSuccess: () => {
        toast.success('Regional office deleted successfully');
        queryClient.invalidateQueries(['regional-offices']);
        navigate('/regional-offices');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to delete regional office');
      }
    }
  );

  const handleDeleteOffice = async () => {
    if (window.confirm('Are you sure you want to delete this regional office? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const handlePresetClick = (preset) => {
    setActivePreset(preset);
    const { startDate, endDate } = getPresetDates(preset);
    setDateRange({ startDate, endDate });
  };

  const handleDateRangeChange = (field, value) => {
    setActivePreset('custom');
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearOverviewFilters = () => {
    handlePresetClick('today');
  };

  const getStatusBadge = (driver) => {
    if (driver.isBlocked) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <span className="w-1.5 h-1.5 mr-1.5 bg-red-500 rounded-full"></span>
          Blocked
        </span>
      );
    }
    if (!driver.isApproved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <span className="w-1.5 h-1.5 mr-1.5 bg-amber-500 rounded-full"></span>
          Pending Approval
        </span>
      );
    }
    if (driver.isAvailable) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <span className="w-1.5 h-1.5 mr-1.5 bg-green-500 rounded-full"></span>
          Online
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <span className="w-1.5 h-1.5 mr-1.5 bg-gray-400 rounded-full"></span>
        Offline
      </span>
    );
  };

  const getTripStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETED':
      case 'DROPPED':
      case 'PAYMENT_COMPLETED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <span className="w-1.5 h-1.5 mr-1.5 bg-green-500 rounded-full"></span>
            {status}
          </span>
        );
      case 'ASSIGNED':
      case 'STARTED':
      case 'IN_PROGRESS':
      case 'PICKEDUP':
      case 'ACCEPTED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <span className="w-1.5 h-1.5 mr-1.5 bg-blue-500 rounded-full"></span>
            {status}
          </span>
        );
      case 'CANCELLED':
      case 'CANCELLED_BEFORE_PICKUP':
      case 'CANCELLED_AFTER_PICKUP':
      case 'REJECTED':
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <span className="w-1.5 h-1.5 mr-1.5 bg-red-500 rounded-full"></span>
            {status}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <span className="w-1.5 h-1.5 mr-1.5 bg-gray-400 rounded-full"></span>
            {status || 'PENDING'}
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <Building2 className="empty-state-icon" />
        <h3 className="empty-state-title">Failed to load regional office</h3>
        <p className="empty-state-description">
          {error?.response?.data?.message || 'An error occurred while loading regional office'}
        </p>
      </div>
    );
  }

  const office = data;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  const searchParams = new URLSearchParams(window.location.search);
                  const fromSource = searchParams.get('from');
                  const sourceId = searchParams.get('sourceId');

                  // Navigate back to the source page
                  if (fromSource === 'trip' && sourceId) {
                    navigate(`/trips/${sourceId}`);
                  } else {
                    navigate('/regional-offices');
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="page-title">{office.name}</h1>
                </div>
                <p className="page-subtitle">Regional Office Details</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {hasPermission('regional_offices', 'write') && (
                <>
                  <Link
                    to={`/regional-offices/${id}/edit`}
                    className="btn btn-info"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Link>
                  <button
                    onClick={handleDeleteOffice}
                    disabled={deleteMutation.isLoading}
                    className="btn btn-danger"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <Building2 className="h-4 w-4" />
              <span>Overview</span>
            </button>
            <button
              onClick={() => handleTabChange('drivers')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === 'drivers'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <Users className="h-4 w-4" />
              <span>Drivers</span>
            </button>
            <button
              onClick={() => handleTabChange('trips')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === 'trips'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <Car className="h-4 w-4" />
              <span>Trips</span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Date Range Filter */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="card-title">Performance Metrics</h3>
                  <button
                    onClick={handleClearOverviewFilters}
                    className="btn btn-outline btn-sm"
                  >
                    Reset Filter
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="flex items-center flex-wrap gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Timeline:</span>
                  </div>

                  <div className="inline-flex rounded-md shadow-sm space-x-1">
                    <button
                      type="button"
                      onClick={() => handlePresetClick('all')}
                      className={`btn btn-sm ${
                        activePreset === 'all'
                          ? 'btn-primary'
                          : 'btn-outline'
                      }`}
                    >
                      All Time
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetClick('today')}
                      className={`btn btn-sm ${
                        activePreset === 'today'
                          ? 'btn-primary'
                          : 'btn-outline'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetClick('yesterday')}
                      className={`btn btn-sm ${
                        activePreset === 'yesterday'
                          ? 'btn-primary'
                          : 'btn-outline'
                      }`}
                    >
                      Yesterday
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetClick('thisMonth')}
                      className={`btn btn-sm ${
                        activePreset === 'thisMonth'
                          ? 'btn-primary'
                          : 'btn-outline'
                      }`}
                    >
                      This Month
                    </button>
                  </div>

                  <div className="flex items-center space-x-2 sm:ml-auto">
                    <span className="text-sm font-medium text-gray-700">Date Range:</span>
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                      className="input input-sm"
                    />
                    <span className="text-gray-500">to</span>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                      className="input input-sm"
                    />
                  </div>
                </div>

                {/* Performance Metrics Grid */}
                {performanceData && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Total Drivers */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Drivers</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {performanceData.totalDrivers || 0}
                          </p>
                        </div>
                        <Users className="h-8 w-8 text-primary-600" />
                      </div>
                    </div>

                    {/* Total Trips */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-1">
                            <p className="text-sm font-medium text-gray-600">Total Trips</p>
                            {activePreset !== 'all' && (
                              <span className="text-xs text-gray-400 font-normal">
                                ({activePreset === 'today' ? 'Today' : activePreset === 'yesterday' ? 'Yesterday' : activePreset === 'thisMonth' ? 'This Month' : 'Filtered'})
                              </span>
                            )}
                          </div>
                          <p className="text-2xl font-bold text-gray-900">
                            {performanceData?.totalTrips ?? office?.totalTrips ?? 0}
                          </p>
                        </div>
                        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                          <Car className="h-6 w-6" />
                        </div>
                      </div>
                    </div>

                    {/* Total Revenue */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                          <p className="text-2xl font-bold text-gray-900">
                            ₹{(performanceData.totalRevenue || 0).toLocaleString()}
                          </p>
                        </div>
                        <span className="text-3xl font-bold text-green-600">₹</span>
                      </div>
                    </div>

                    {/* Active Drivers */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Active Drivers</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {performanceData.activeDrivers || 0}
                          </p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Trip Status Breakdown */}
                {performanceData && (
                  <div className="mt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Trip Status Breakdown</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-600">Completed</p>
                            <p className="text-2xl font-bold text-green-900">
                              {performanceData.completedTrips || 0}
                            </p>
                          </div>
                          <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-600">Ongoing</p>
                            <p className="text-2xl font-bold text-blue-900">
                              {performanceData.ongoingTrips || 0}
                            </p>
                          </div>
                          <Clock className="h-8 w-8 text-blue-600" />
                        </div>
                      </div>

                      <div className="bg-red-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-red-600">Cancelled</p>
                            <p className="text-2xl font-bold text-red-900">
                              {performanceData.cancelledTrips || 0}
                            </p>
                          </div>
                          <XCircle className="h-8 w-8 text-red-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Office Information Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Basic Information */}
              <div className="lg:col-span-2 space-y-6">
                {/* Office Details */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Office Information</h3>
                  </div>
                  <div className="card-body">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Office Name</label>
                        <p className="text-gray-900 font-medium">{office.name}</p>
                      </div>

                      <div>
                        <label className="form-label">Status</label>
                        <span className={`badge ${office.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {office.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="md:col-span-2">
                        <label className="form-label">Address</label>
                        <p className="text-gray-900">{office.address}</p>
                      </div>

                      <div>
                        <label className="form-label">Location</label>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {office.location?.coordinates?.[1]?.toFixed(6)}, {office.location?.coordinates?.[0]?.toFixed(6)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="form-label">Serving Areas</label>
                        <p className="text-gray-900">{office.servingAreas?.length || 0} areas</p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between">
                          <label className="form-label mb-0">Total Trips</label>
                          {activePreset !== 'all' && (
                            <span className="text-xs text-primary-600 font-medium">
                              ({activePreset === 'today' ? 'Today' : activePreset === 'yesterday' ? 'Yesterday' : activePreset === 'thisMonth' ? 'This Month' : `${dateRange.startDate || ''} to ${dateRange.endDate || ''}`})
                            </span>
                          )}
                        </div>
                        <p className="text-gray-900 font-semibold text-base mt-0.5">
                          {(performanceData?.totalTrips ?? office?.totalTrips ?? 0)} trips
                        </p>
                      </div>

                      <div>
                        <label className="form-label">Acting Driver Service</label>
                        <span className={`badge ${office.enabledServices?.includes('ACTING') ? 'badge-success' : 'badge-secondary'}`}>
                          {office.enabledServices?.includes('ACTING') ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Contact Information</h3>
                  </div>
                  <div className="card-body">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {office.contactInfo?.phone && (
                        <div className="flex items-center space-x-3">
                          <Phone className="h-5 w-5 text-gray-400" />
                          <div>
                            <label className="form-label">Phone</label>
                            <p className="text-gray-900">{office.contactInfo.phone}</p>
                          </div>
                        </div>
                      )}

                      {office.contactInfo?.email && (
                        <div className="flex items-center space-x-3">
                          <Mail className="h-5 w-5 text-gray-400" />
                          <div>
                            <label className="form-label">Email</label>
                            <p className="text-gray-900">{office.contactInfo.email}</p>
                          </div>
                        </div>
                      )}

                      {office.contactInfo?.website && (
                        <div className="flex items-center space-x-3">
                          <Globe className="h-5 w-5 text-gray-400" />
                          <div>
                            <label className="form-label">Website</label>
                            <a
                              href={office.contactInfo.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800"
                            >
                              {office.contactInfo.website}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Admin Information */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Admin Account</h3>
                  </div>
                  <div className="card-body">
                    <div className="space-y-3">
                      <div>
                        <label className="form-label">Admin Name</label>
                        <p className="text-gray-900">{office.adminUser?.name || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="form-label">Admin Email</label>
                        <p className="text-gray-900">{office.adminUser?.email || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="form-label">Created By</label>
                        <p className="text-gray-900">{office.createdBy?.name || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="form-label">Created On</label>
                        <p className="text-gray-900">
                          {new Date(office.createdOn).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="card-title">Driver Filters</h3>
                  <button
                    onClick={clearDriverFilters}
                    className="btn btn-outline btn-sm"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="form-label">Search</label>
                    <input
                      type="text"
                      placeholder="Search drivers..."
                      value={driverFilters.search}
                      onChange={(e) => handleDriverFilterChange('search', e.target.value)}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Status</label>
                    <select
                      value={driverFilters.status}
                      onChange={(e) => handleDriverFilterChange('status', e.target.value)}
                      className="input"
                    >
                      <option value="">All Status</option>
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                      <option value="busy">Busy</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Approval</label>
                    <select
                      value={driverFilters.isApproved}
                      onChange={(e) => handleDriverFilterChange('isApproved', e.target.value)}
                      className="input"
                    >
                      <option value="">All</option>
                      <option value="true">Approved</option>
                      <option value="false">Pending</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Block Status</label>
                    <select
                      value={driverFilters.isBlocked}
                      onChange={(e) => handleDriverFilterChange('isBlocked', e.target.value)}
                      className="input"
                    >
                      <option value="">All</option>
                      <option value="true">Blocked</option>
                      <option value="false">Active</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Drivers Table */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Assigned Drivers</h3>
              </div>
              <div className="card-body">
                {driversLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="loading-spinner"></div>
                  </div>
                ) : driversData?.drivers && driversData.drivers.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Driver</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vehicle</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Rating</th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {driversData.drivers.map((driver) => (
                            <tr key={driver._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center border border-primary-200">
                                    <span className="text-sm font-semibold text-primary-700">
                                      {driver.name?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="font-semibold text-gray-900">{driver.name}</div>
                                    <div className="text-xs text-gray-400 font-mono mt-0.5">{driver.licenseNo}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{driver.phone}</td>
                              <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(driver)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {driver.vehicleId ? (
                                  <div className="text-sm">
                                    <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200 uppercase tracking-wider mb-1 font-mono">
                                      {driver.vehicleId.regNo}
                                    </div>
                                    <div className="text-gray-500 text-xs font-medium">{driver.vehicleId.make} {driver.vehicleId.model}</div>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs italic">No vehicle</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-1">
                                  <Star className="h-4 w-4 text-amber-400 fill-current" />
                                  <span className="text-sm font-semibold text-gray-900">{driver.ratingData?.currentrating?.toFixed(1) || '0.0'}</span>
                                  <span className="text-xs text-gray-400 font-medium">/ 5.0</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-2">
                                  <Link
                                    to={`/drivers/${driver._id}`}
                                    state={{ from: `/regional-offices/${id}?tab=drivers` }}
                                    className="inline-flex items-center p-1.5 border border-transparent rounded-full text-gray-400 hover:text-primary-600 hover:bg-gray-100 focus:outline-none transition-colors"
                                    title="View Driver"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {driversData.pagination && driversData.pagination.total > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-700">
                          Showing {((driversData.pagination.current - 1) * driversData.pagination.limit) + 1} to{' '}
                          {Math.min(driversData.pagination.current * driversData.pagination.limit, driversData.pagination.totalRecords)} of{' '}
                          {driversData.pagination.totalRecords} results
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleDriverPageChange(driversData.pagination.current - 1)}
                            disabled={driversData.pagination.current === 1}
                            className="btn btn-outline btn-sm"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handleDriverPageChange(driversData.pagination.current + 1)}
                            disabled={driversData.pagination.current === driversData.pagination.total}
                            className="btn btn-outline btn-sm"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No drivers found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Trips Tab */}
        {activeTab === 'trips' && (
          <div className="space-y-6">
            {/* Trip Filters */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="card-title">Trip Filters</h3>
                  <button
                    onClick={clearTripFilters}
                    className="btn btn-outline btn-sm"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="form-label">Search</label>
                    <input
                      type="text"
                      placeholder="Search passenger, phone, address..."
                      value={tripFilters.search}
                      onChange={(e) => handleTripFilterChange('search', e.target.value)}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Status</label>
                    <select
                      value={tripFilters.status}
                      onChange={(e) => handleTripFilterChange('status', e.target.value)}
                      className="input"
                    >
                      <option value="">All Statuses</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="ONGOING">Ongoing / Active</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="PENDING">Pending</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      value={tripFilters.startDate}
                      onChange={(e) => handleTripFilterChange('startDate', e.target.value)}
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      value={tripFilters.endDate}
                      onChange={(e) => handleTripFilterChange('endDate', e.target.value)}
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Trips Table */}
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="card-title">Regional Office Trips</h3>
                <span className="text-sm text-gray-500 font-medium">
                  Total: {tripsData?.pagination?.totalRecords ?? office?.totalTrips ?? 0} trips
                </span>
              </div>
              <div className="card-body">
                {tripsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="loading-spinner"></div>
                  </div>
                ) : tripsData?.trips && tripsData.trips.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Passenger / Trip</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vehicle</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Driver</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fare</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tripsData.trips.map((trip) => {
                            const pickupStop = trip.stops?.[0] || trip.stops?.find(s => s.name?.toLowerCase().includes('pickup'));
                            const dropStop = trip.stops?.[trip.stops?.length - 1] || trip.stops?.find(s => s.name?.toLowerCase().includes('drop'));
                            const pickupAddress = pickupStop?.address || trip.from?.address || 'Pickup Point';
                            const dropAddress = dropStop?.address || trip.to?.address || 'Drop Point';
                            const tripTime = trip.bookingTime || trip.createdOn || (trip.createdAt ? new Date(trip.createdAt).getTime() : null);

                            return (
                              <tr key={trip._id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex flex-col">
                                    <div className="font-semibold text-gray-900">
                                      {trip.bookingForName || trip.userName || 'Passenger'}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono">
                                      {trip.bookingForPhone || trip.phoneNumber || 'N/A'}
                                    </div>
                                    {tripTime && (
                                      <div className="text-xs text-gray-400 mt-1 flex items-center">
                                        <Clock className="h-3 w-3 mr-1" />
                                        {new Date(tripTime).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="max-w-xs space-y-1 text-xs">
                                    <div className="flex items-start space-x-1.5">
                                      <span className="w-2 h-2 rounded-full bg-green-500 mt-1 flex-shrink-0"></span>
                                      <span className="text-gray-700 font-medium truncate" title={pickupAddress}>
                                        {pickupAddress}
                                      </span>
                                    </div>
                                    <div className="flex items-start space-x-1.5">
                                      <span className="w-2 h-2 rounded-full bg-red-500 mt-1 flex-shrink-0"></span>
                                      <span className="text-gray-500 truncate" title={dropAddress}>
                                        {dropAddress}
                                      </span>
                                    </div>
                                    {trip.estimatedDistance && (
                                      <div className="text-gray-400 text-xs">
                                        {trip.estimatedDistance} km • {trip.estimatedDuration || 0} mins
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-xs font-semibold uppercase px-2 py-1 bg-gray-100 rounded inline-block text-gray-700">
                                    {trip.vehicleType || trip.actingVehicleType || 'Standard'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {trip.driverId ? (
                                    <div className="text-sm">
                                      <div className="font-medium text-gray-900">
                                        {trip.driverId.name || 'Driver'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {trip.driverId.phone || ''}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">Unassigned</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-semibold text-gray-900">
                                    ₹{trip.fare || trip.finalFare || trip.minFare || 0}
                                  </div>
                                  <div className="text-xs text-gray-500 uppercase">
                                    {trip.paymentMethod || 'CASH'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {getTripStatusBadge(trip.status)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <Link
                                    to={`/trips/${trip._id}`}
                                    state={{ from: `/regional-offices/${id}?tab=trips` }}
                                    className="inline-flex items-center p-1.5 border border-transparent rounded-full text-gray-400 hover:text-primary-600 hover:bg-gray-100 focus:outline-none transition-colors"
                                    title="View Trip"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Link>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {tripsData.pagination && tripsData.pagination.total > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-700">
                          Showing {((tripsData.pagination.current - 1) * tripsData.pagination.limit) + 1} to{' '}
                          {Math.min(tripsData.pagination.current * tripsData.pagination.limit, tripsData.pagination.totalRecords)} of{' '}
                          {tripsData.pagination.totalRecords} results
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleTripPageChange(tripsData.pagination.current - 1)}
                            disabled={tripsData.pagination.current === 1}
                            className="btn btn-outline btn-sm"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handleTripPageChange(tripsData.pagination.current + 1)}
                            disabled={tripsData.pagination.current === tripsData.pagination.total}
                            className="btn btn-outline btn-sm"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Car className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No trips found for this regional office</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegionalOfficeDetails; 